/**
 * GS1 DataMatrix Barcode Parser for Vaccine Products
 *
 * Parses GS1 DataMatrix barcodes commonly found on vaccine packaging.
 * Supports Application Identifiers (AI) for:
 * - (01) GTIN - Global Trade Item Number
 * - (17) Expiration Date - YYMMDD format
 * - (10) Lot Number - Alphanumeric
 * - (21) Serial Number - Alphanumeric (optional)
 */

export interface ParsedBarcodeData {
  gtin: string | null
  lotNumber: string | null
  expirationDate: string | null // YYYY-MM-DD format
  serialNumber: string | null
  raw: string
}

const GS1_GROUP_SEPARATOR = String.fromCharCode(29) // ASCII 29

/**
 * Parse a GS1 DataMatrix barcode string
 *
 * Supports multiple formats:
 * 1. With AIs in parentheses: (01)03456789012345(17)251231(10)ABC123
 * 2. With ASCII 29 separator: 0103456789012345<GS>17251231<GS>10ABC123
 * 3. With DataMatrix prefix: ]d201034567890123451712253110ABC123
 *
 * @param barcode - Raw barcode string from USB scanner
 * @returns Parsed barcode data with GTIN, lot, expiration, serial
 */
export function parseGS1Barcode(barcode: string): ParsedBarcodeData {
  if (!barcode || typeof barcode !== 'string') {
    return {
      gtin: null,
      lotNumber: null,
      expirationDate: null,
      serialNumber: null,
      raw: barcode || '',
    }
  }

  // Remove DataMatrix prefix if present (]d2, ]C1, etc)
  const cleanBarcode = barcode.replace(/^\]([dDcC][012])?/, '')

  const result: ParsedBarcodeData = {
    gtin: null,
    lotNumber: null,
    expirationDate: null,
    serialNumber: null,
    raw: barcode,
  }

  // Try parsing with parentheses format first
  if (cleanBarcode.includes('(')) {
    parseWithParentheses(cleanBarcode, result)
  } else {
    // Try parsing with GS separator or fixed positions
    parseWithoutParentheses(cleanBarcode, result)
  }

  return result
}

/**
 * Parse barcode with AI in parentheses format: (01)12345(17)251231(10)LOT
 */
function parseWithParentheses(
  barcode: string,
  result: ParsedBarcodeData
): void {
  // Match (AI)VALUE patterns
  const aiPattern = /\((\d{2,4})\)([^(]*?)(?=\(|$)/g
  let match: RegExpExecArray | null

  while (true) {
    match = aiPattern.exec(barcode)
    if (!match) {
      break
    }
    const ai = match[1]
    const value = match[2]?.trim()

    if (!value) {
      continue
    }

    switch (ai) {
      case '01': // GTIN
        result.gtin = value.padStart(14, '0')
        break
      case '17': // Expiration date
        result.expirationDate = parseExpirationDate(value)
        break
      case '10': // Lot number
        result.lotNumber = value
        break
      case '21': // Serial number
        result.serialNumber = value
        break
    }
  }
}

/**
 * Parse barcode without parentheses (fixed positions or GS separator)
 * Format: 0103456789012345<GS>17251231<GS>10ABC123
 */
function parseWithoutParentheses(
  barcode: string,
  result: ParsedBarcodeData
): void {
  // Split by group separator if present
  const parts = barcode.split(GS1_GROUP_SEPARATOR)

  for (const part of parts) {
    if (!part || part.length < 3) {
      continue
    }

    const ai = part.substring(0, 2)
    const value = part.substring(2)

    switch (ai) {
      case '01': // GTIN - 14 digits
        if (value.length >= 14) {
          result.gtin = value.substring(0, 14)
        }
        break
      case '17': // Expiration date - 6 digits
        if (value.length >= 6) {
          result.expirationDate = parseExpirationDate(value.substring(0, 6))
        }
        break
      case '10': // Lot number - variable length
        result.lotNumber = value
        break
      case '21': // Serial number - variable length
        result.serialNumber = value
        break
    }
  }

  // If only GTIN extracted (or nothing), try to parse as concatenated format
  if (!result.lotNumber && !result.expirationDate && barcode.length >= 16) {
    // Try to extract GTIN from first 16 characters (01 + 14 digit GTIN)
    if (barcode.startsWith('01')) {
      result.gtin = barcode.substring(2, 16)

      // Look for remaining AIs
      const remaining = barcode.substring(16)
      if (remaining.startsWith('17') && remaining.length >= 8) {
        result.expirationDate = parseExpirationDate(remaining.substring(2, 8))

        // Check for lot number
        const afterExp = remaining.substring(8)
        if (afterExp.startsWith('10')) {
          result.lotNumber = afterExp.substring(2)
        }
      } else if (remaining.startsWith('10')) {
        // Lot number comes first - find where it ends (look for next AI like "17")
        const expIndex = remaining.indexOf('17', 2) // Start looking after "10"

        if (expIndex !== -1) {
          // Extract lot number between "10" and "17"
          result.lotNumber = remaining.substring(2, expIndex)

          // Parse expiration date after "17"
          const expPart = remaining.substring(expIndex)
          if (expPart.length >= 8) {
            result.expirationDate = parseExpirationDate(expPart.substring(2, 8))
          }
        } else {
          // No expiration date found, rest is lot number
          result.lotNumber = remaining.substring(2)
        }
      }
    }
  }
}

/**
 * Parse expiration date from YYMMDD format to YYYY-MM-DD
 *
 * @param dateStr - Date string in YYMMDD format (e.g., "251231" for Dec 31, 2025)
 * @returns ISO date string YYYY-MM-DD or null if invalid
 */
function parseExpirationDate(dateStr: string): string | null {
  if (!dateStr || dateStr.length !== 6) {
    return null
  }

  const yy = dateStr.substring(0, 2)
  const mm = dateStr.substring(2, 4)
  const dd = dateStr.substring(4, 6)

  // Convert YY to YYYY (assume 2000-2099)
  const year = 2000 + parseInt(yy, 10)

  // Validate month and day
  const month = parseInt(mm, 10)
  const day = parseInt(dd, 10)

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null
  }

  // Format as YYYY-MM-DD
  return `${year}-${mm}-${dd}`
}

/**
 * Convert GTIN-14 to NDC-11 format
 *
 * GTIN-14 structure for pharmaceutical NDC in US:
 * - Positions 0-2: Prefix (typically "003" for pharma)
 * - Positions 3-13: NDC-11 digits (11 digits)
 *
 * Example: GTIN 00300051971053 → NDC 00005-1971-05
 *
 * NDC-11 format: 5-4-2 (e.g., 00005-1971-05)
 *
 * @param gtin - 14-digit GTIN
 * @returns NDC-11 and NDC-10 formats, or null if invalid
 */
export function gtinToNDC(gtin: string): {
  ndc11: string
  ndc10: string
} | null {
  if (!gtin || gtin.length !== 14) {
    return null
  }

  // GTIN format: positions 0-2 are prefix (e.g., "003" for pharma)
  // Positions 3-13 contain the 11-digit NDC
  const ndcDigits = gtin.substring(3, 14)

  // Split into 5-4-2 format for NDC-11
  const segment1 = ndcDigits.substring(0, 5)
  const segment2 = ndcDigits.substring(5, 9)
  const segment3 = ndcDigits.substring(9, 11)

  const ndc11 = `${segment1}-${segment2}-${segment3}`

  // For NDC-10, remove leading zero from first segment if present
  const segment1Stripped = segment1.replace(/^0+/, '') || '0'
  const ndc10 = `${segment1Stripped}-${segment2}-${segment3}`

  return { ndc11, ndc10 }
}

/**
 * Normalize NDC format (remove dashes, pad to 11 digits)
 *
 * @param ndc - NDC string with or without dashes
 * @returns Normalized 11-digit NDC or null if invalid
 */
export function normalizeNDC(ndc: string): string | null {
  if (!ndc) {
    return null
  }

  // Remove dashes and spaces
  const digits = ndc.replace(/[-\s]/g, '')

  // Must be 10 or 11 digits
  if (digits.length < 10 || digits.length > 11) {
    return null
  }

  // Pad to 11 digits if needed
  return digits.padStart(11, '0')
}
